import { useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { useCallTranscript } from "../../hooks/useCallTranscript";
import { formatDateShort } from "../../utils/formatters";
import type { TranscriptTurnDetail } from "../../types/transcriptSearch";

// =============================================================================
// TranscriptPanel
// =============================================================================
// PURPOSE: Renders a single call transcript for the LeadTimelineModal right tab.
//
// LAYOUT:
//   Header strip — title + recorded_at + share_url icon + action_items count
//   Sub-line    — recorded_by_email
//   Invitees    — chip row (ds-accent=internal, ds-amber=external)
//   Summary     — collapsible <details> with ReactMarkdown + action items list
//   Turn list   — scrollable, each turn: HMS timestamp deep-link, speaker badge,
//                 name, and text. Anchor turn highlighted + scrollIntoView.
//
// GOTCHAS:
//   - timestamp button: e.stopPropagation() to prevent modal close bubbling
//   - anchorTurnRef: scrollIntoView({block:'center', behavior:'auto'}), never
//     scrollTop=scrollHeight (feedback_proof_grid_scroll_pattern.md)
//   - No setState in the scrollIntoView useEffect — safe for react-hooks/set-state-in-effect
//   - useCallTranscript takes (recordingId: number|null, orgId: string|null)
// =============================================================================

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatHMS(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  return h > 0
    ? `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
    : `${m}:${s.toString().padStart(2, "0")}`;
}

function getActionItemText(item: unknown): string {
  if (typeof item === "string") return item;
  if (item && typeof item === "object") {
    const obj = item as Record<string, unknown>;
    if (typeof obj.text === "string") return obj.text;
    try {
      return JSON.stringify(item);
    } catch {
      return String(item);
    }
  }
  return String(item);
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  recordingId: number;
  organizationId: string;
  orgTz: string;
  anchorTurnIndex?: number;
}

// ── Sub-component: single turn row ────────────────────────────────────────────

interface TurnRowProps {
  turn: TranscriptTurnDetail;
  shareUrl: string;
  isAnchor: boolean;
  anchorRef: React.RefObject<HTMLDivElement | null>;
}

function TurnRow({ turn, shareUrl, isAnchor, anchorRef }: TurnRowProps) {
  const deepLink = `${shareUrl}#t=${turn.timestamp_secs}s`;

  const speakerBadgeClass =
    turn.is_closer === true
      ? "text-ds-accent"
      : turn.is_closer === false
        ? "text-ds-amber"
        : "text-glass-muted";

  const speakerLabel =
    turn.is_closer === true ? "closer" : turn.is_closer === false ? "lead" : "(unknown)";

  return (
    <div
      ref={isAnchor ? anchorRef : undefined}
      className={`px-3 py-1.5 rounded transition-colors ${
        isAnchor ? "bg-ds-accent/10 border-l-2 border-ds-accent pl-2" : "hover:bg-black/[0.03]"
      }`}
    >
      {/* Top row: timestamp + speaker badge + speaker name */}
      <div className="flex items-center gap-2 mb-0.5">
        <button
          onClick={(e) => {
            e.stopPropagation();
            window.open(deepLink, "_blank", "noopener,noreferrer");
          }}
          className="text-ds-accent text-xs font-mono cursor-pointer hover:underline shrink-0"
          title={`Open at ${formatHMS(turn.timestamp_secs)}`}
        >
          {formatHMS(turn.timestamp_secs)}
        </button>

        <span className={`text-xs font-semibold uppercase tracking-wide ${speakerBadgeClass}`}>
          {speakerLabel}
        </span>

        {turn.speaker_name && (
          <span className="text-glass text-xs font-medium truncate">{turn.speaker_name}</span>
        )}
      </div>

      {/* Turn text — text-xs to match the timeline + Summary; leading-snug keeps
          the transcript short/dense. */}
      <p className="text-glass text-xs leading-snug whitespace-pre-wrap pl-0">{turn.text}</p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function TranscriptPanel({ recordingId, organizationId, orgTz, anchorTurnIndex }: Props) {
  const { transcript, loading, error } = useCallTranscript(recordingId, organizationId);

  // Ref for the anchor turn — scroll to it on mount once transcript loads.
  // Using a ref here (not state) — pure side-effect, no re-render needed.
  // DO NOT use scrollTop=scrollHeight (memory: feedback_proof_grid_scroll_pattern.md)
  const anchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (anchorRef.current) {
      anchorRef.current.scrollIntoView({ block: "center", behavior: "auto" });
    }
  }, [transcript]); // fires after transcript loads and anchor is rendered

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="text-glass-muted text-sm">Loading transcript…</span>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="py-6 px-4">
        <p className="text-ds-red text-sm">Failed to load transcript: {error}</p>
      </div>
    );
  }

  // ── Null transcript ────────────────────────────────────────────────────────
  if (!transcript) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="text-glass-muted text-sm">Transcript not available.</span>
      </div>
    );
  }

  // ── Derived values ─────────────────────────────────────────────────────────
  const recordedAtDate = new Date(transcript.recorded_at);
  const formattedDate = formatDateShort(recordedAtDate, orgTz);

  const actionItems = Array.isArray(transcript.action_items) ? transcript.action_items : [];
  const invitees = Array.isArray(transcript.calendar_invitees)
    ? transcript.calendar_invitees.filter((inv) => inv && (inv.email || inv.name))
    : [];

  // ── Full transcript render ─────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full">
      {/* ── Header strip ──────────────────────────────────────────────────── */}
      <div className="px-4 pt-4 pb-3 border-b border-black/[0.08] shrink-0">
        <div className="flex items-start justify-between gap-2">
          {/* Title + date */}
          <div className="min-w-0">
            <span className="text-glass font-semibold text-base block truncate">
              {transcript.meeting_title ?? "Untitled Recording"}
            </span>
            <span className="text-glass-muted text-xs">{formattedDate}</span>
          </div>

          {/* Right side: action items count + external link */}
          <div className="flex items-center gap-2 shrink-0">
            {actionItems.length > 0 && (
              <span className="text-glass-muted text-xs">
                {actionItems.length} action item{actionItems.length !== 1 ? "s" : ""}
              </span>
            )}
            {transcript.share_url && (
              <a
                href={transcript.share_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-ds-accent hover:opacity-70 transition-opacity"
                title="Open in Fathom"
                onClick={(e) => e.stopPropagation()}
              >
                {/* External link icon */}
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
            )}
          </div>
        </div>

        {/* Recorded-by sub-line */}
        {transcript.recorded_by_email && (
          <p className="text-glass-muted text-xs mt-1">{transcript.recorded_by_email}</p>
        )}
      </div>

      {/* ── Calendar invitees ─────────────────────────────────────────────── */}
      {invitees.length > 0 && (
        <div className="px-4 py-2 border-b border-black/[0.08] flex flex-wrap gap-1.5 shrink-0">
          {invitees.map((inv, idx) => {
            const label = inv.name ?? inv.email ?? "Unknown";
            const display = label.length > 30 ? `${label.slice(0, 30)}…` : label;
            const isExternal = inv.is_external === true;

            return (
              <span
                key={idx}
                className={`px-2 py-1 text-xs rounded ${
                  isExternal ? "bg-ds-amber/10 text-ds-amber" : "bg-ds-accent/10 text-ds-accent"
                }`}
                title={inv.email ?? label}
              >
                {display}
              </span>
            );
          })}
        </div>
      )}

      {/* ── Summary (collapsible) ─────────────────────────────────────────── */}
      {transcript.summary_markdown && (
        <div className="px-4 py-3 border-b border-black/[0.08] shrink-0">
          <details>
            <summary className="text-glass font-medium text-sm cursor-pointer select-none">
              Summary
            </summary>
            {/* Match the transcript-turn font: Inter, text-sm, leading-relaxed.
                `prose`/`prose-sm` are dead classes here (@tailwindcss/typography
                is not configured), so without these overrides the markdown's
                headings render at raw browser-default sizes — that's the "wrong
                font" look. Every element is normalized to the timeline's font. */}
            <div className="mt-2 max-w-none text-glass-muted text-xs leading-relaxed space-y-1.5">
              <ReactMarkdown
                components={{
                  h1: ({ children }) => (
                    <p className="text-glass font-semibold text-xs mt-2">{children}</p>
                  ),
                  h2: ({ children }) => (
                    <p className="text-glass font-semibold text-xs mt-2">{children}</p>
                  ),
                  h3: ({ children }) => (
                    <p className="text-glass font-semibold text-xs mt-2">{children}</p>
                  ),
                  p: ({ children }) => (
                    <p className="text-glass-muted text-xs leading-relaxed">{children}</p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-glass">{children}</strong>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc list-inside space-y-1">{children}</ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal list-inside space-y-1">{children}</ol>
                  ),
                  li: ({ children }) => <li className="text-glass-muted">{children}</li>,
                  // Fathom summaries wrap every sentence in a share link. Inherit
                  // the muted body color so the summary reads like the timeline
                  // (not a wall of blue); go accent + underline only on hover.
                  a: ({ children, href }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="text-inherit hover:text-ds-accent hover:underline"
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {transcript.summary_markdown}
              </ReactMarkdown>
            </div>

            {actionItems.length > 0 && (
              <ul className="mt-2 space-y-1 list-disc list-inside">
                {actionItems.map((item, idx) => (
                  <li key={idx} className="text-glass-muted text-xs">
                    {getActionItemText(item)}
                  </li>
                ))}
              </ul>
            )}
          </details>
        </div>
      )}

      {/* ── Turn list ─────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-1 pt-2">
        {transcript.turns.length === 0 ? (
          <p className="text-glass-muted text-sm py-4 text-center">No transcript turns.</p>
        ) : (
          transcript.turns.map((turn) => {
            const isAnchor = anchorTurnIndex !== undefined && turn.turn_index === anchorTurnIndex;
            return (
              <TurnRow
                key={turn.turn_index}
                turn={turn}
                shareUrl={transcript.share_url}
                isAnchor={isAnchor}
                anchorRef={anchorRef}
              />
            );
          })
        )}
      </div>
    </div>
  );
}
